import React, { useRef } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { SIGN_AND_ATTACH, COMPLETE_AND_ATTACH, ATTACH } from './types/ContractSettingsFieldType';

import { truncateFileName } from 'Helpers/misc';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import ModalFooter from 'CommonComponents/ModalFooter';
import SelectFromField from 'CommonComponents/SelectFromField';
import TextareaFormField from 'CommonComponents/TextareaFormField';
import TextButton from 'CommonComponents/TextButton';
import TextFormField from 'CommonComponents/TextFormField';

import useApiRequest from 'Hooks/useApiRequest';

import TrashIcon from 'Assets/icons/trash.svg';

const ACTION_CREATE = 'create';
const ACTION_UPDATE = 'update';

const FILE_TYPES = {
    [SIGN_AND_ATTACH]: ['application/pdf'],
    [COMPLETE_AND_ATTACH]: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    [ATTACH]: [],
};

const fileValidation = Yup.mixed()
    .test('fileRequired', 'At least one file is required', (files, { parent }) => {
        if (parent.type === ATTACH || parent.existedAttachments.length > 0) {
            return true;
        }
        return files && files.length > 0;
    })
    .test('fileType', 'Invalid file format', (files, { parent }) => {
        if (parent.type === ATTACH) {
            return true;
        }
        if (!files || files.length === 0) {
            return true;
        }
        const allowedTypes = FILE_TYPES[parent.type] || [];
        return files.every((file) => allowedTypes.includes(file.type));
    });

const FieldCreateSchema = Yup.object().shape({
    name: Yup.string().max(200).required('This value is required'),
    instruction: Yup.string().max(400).nullable().notRequired(),
    type: Yup.string().required('This value is required'),
    files: fileValidation,
});

const FieldUpdateSchema = Yup.object().shape({
    name: Yup.string().max(200).required('This value is required'),
    instruction: Yup.string().max(400).nullable().notRequired(),
    type: Yup.string().required('This value is required'),
    state: Yup.string().required('This value is required'),
    files: fileValidation,
});

const options = {
    headers: {
        'Content-Type': 'multipart/form-data',
    },
};

export default function SupplierPortalSettingsFieldCreateForm(props) {
    const fileInputRef = useRef(null);
    const action = props.field ? ACTION_UPDATE : ACTION_CREATE;

    const request = useApiRequest({ url: props.saveUrl, options });

    const resetFiles = (event, setFieldValue, ref) => {
        setFieldValue('type', event.target.value);
        setFieldValue('files', []);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeExistedAttachment = (attachment, setFieldValue, values) => {
        const existedItems = JSON.parse(JSON.stringify(values.existedAttachments));
        const toRemoveIds = JSON.parse(JSON.stringify(values.attachmentsToDelete));

        const itemToRemove = existedItems.find((item) => {
            return item.id === attachment.id;
        });

        if (itemToRemove) {
            toRemoveIds.push(itemToRemove.id);
            const newList = existedItems.filter((item) => {
                return item.id !== itemToRemove.id;
            });

            setFieldValue('attachmentsToDelete', toRemoveIds);
            setFieldValue('existedAttachments', newList);
        }
    };

    const initValues = {
        name: props.field ? props.field.name : '',
        instruction: props.field ? props.field.instruction || '' : '',
        type: props.field ? props.field.type : props.types[0].value || '',
        files: [],
        existedAttachments: [],
        attachmentsToDelete: [],
    };

    if (action === ACTION_UPDATE) {
        initValues['state'] = props.field.state;
        initValues['existedAttachments'] = props.field.attachments;
    }

    return (
        <Formik
            initialValues={initValues}
            validationSchema={action === ACTION_UPDATE ? FieldUpdateSchema : FieldCreateSchema}
            onSubmit={(values, actions) => {
                const formData = new FormData();
                formData.append('name', values.name);
                formData.append('instruction', values.instruction);
                formData.append('type', values.type);

                values.files.forEach((file) => {
                    formData.append('files[]', file);
                });
                if (action === ACTION_UPDATE) {
                    formData.append('state', values.state);
                    formData.append('toRemove', values.attachmentsToDelete);
                }

                request
                    .post(formData)
                    .then((response) => {
                        actions.resetForm();
                        actions.setSubmitting(false);
                        props.fetchData();
                        props.internalSuccessHandler();
                    })
                    .catch((e) => {
                        actions.setSubmitting(false);
                        actions.setErrors(e.response.data.errors);
                    });
            }}
        >
            {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                <>
                    <div className="tw-grid tw-grid-cols-1">
                        <TextFormField label={$t('Name')} type="text" required={true} name="name" placeholder="Enter field name"></TextFormField>
                    </div>

                    <div className="tw-grid tw-grid-cols-1">
                        <TextareaFormField
                            label={$t('Instructions')}
                            as="textarea"
                            type="text"
                            name="instruction"
                            placeholder={$t('Please attach your WC insurance certificate of currency')}
                        ></TextareaFormField>
                    </div>

                    <div className="tw-grid tw-grid-cols-1">
                        <SelectFromField label={$t('Type')} as="select" name="type" onChange={(event) => resetFiles(event, setFieldValue, fileInputRef)}>
                            {props.types.map((item) => (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            ))}
                        </SelectFromField>
                    </div>

                    {action === ACTION_UPDATE && (
                        <>
                            <div className="tw-grid tw-grid-cols-1">
                                <SelectFromField label={$t('Status')} as="select" name="state">
                                    {props.states.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </SelectFromField>
                            </div>
                            {props.field.attachments.length > 0 && (
                                <div className="table__card tw-space-y-6 tw-mt-4">
                                    <div className="tw-grid tw-grid-cols-1">
                                        <div className="tw-mb-2">{$t('Existing attachments')}:</div>
                                        <ul className="tw-pl-3 tw-pb-2">
                                            {values.existedAttachments.map((attachment) => {
                                                return (
                                                    <li key={attachment.id}>
                                                        {truncateFileName(attachment.name)}{' '}
                                                        <span className="tw-ml-4">
                                                            <TextButton
                                                                variant="danger"
                                                                onClick={() => removeExistedAttachment(attachment, setFieldValue, values)}
                                                            >
                                                                <TrashIcon className="tw-w-3 tw-h-3" />
                                                            </TextButton>
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {values.type !== ATTACH && (
                        <>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={FILE_TYPES[values.type]?.join(', ')}
                                onChange={(event) => {
                                    const files = Array.from(event.currentTarget.files);
                                    setFieldValue('files', files);
                                }}
                            />
                            {errors.files && <div style={{ color: 'red' }}>{errors.files}</div>}

                            {values.files.length > 0 && values.type !== ATTACH && (
                                <div>
                                    <ul>
                                        {values.files.map((file, index) => (
                                            <li key={index}>{truncateFileName(file.name)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                    {isSubmitting && <LoadingSpinner />}

                    <ModalFooter
                        cancelButtonText={$t('Cancel')}
                        primaryButtonText={action === ACTION_CREATE ? $t('Add') : $t('Update')}
                        onCancel={props.close}
                        handleSubmit={handleSubmit}
                        isSubmitting={props.request || isSubmitting}
                        submitButtonVariant={'primary'}
                    ></ModalFooter>
                </>
            )}
        </Formik>
    );
}
